import {
  CSSProperties,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import styles from "./styles.module.scss";
import { Popup, Video } from "components";
import { Button } from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import { useAppDispatch } from "hooks";
import { setPageType, setPageTitle } from "store";
import { PageType } from "types";
import { TextArea } from "@progress/kendo-react-inputs";
import { SettingsContext } from "context";
import { useNavigate } from "react-router-dom";
import { FSI_DEMOS } from "common";

const { BlobBaseUrl, APIUrl } = window.config;

interface Props {
  visible: boolean;
  setVisible: Dispatch<SetStateAction<boolean>>;
  generateSummary: () => void;
}

export const CallCenter: FC<Props> = ({ setVisible, visible }) => {
  const dispatch = useAppDispatch();
  const [isEnded, setIsEnded] = useState(false);
  const [summary, setSummary] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { currentDemo } = useContext(SettingsContext);

  const getSummary = () => {
    setIsLoading(true);

    let myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    let raw = JSON.stringify({
      prompt:
        currentDemo?.callCenterScript + "Create a summary of the conversation.",
      temperature: 0.9,
      max_tokens: 1200,
      top_p: 1,
      frequency_penalty: 0,
      presence_penalty: 0,
      best_of: 1,
      stop: null,
    });

    let requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
    };

    return fetch(APIUrl + "/api/summarize-conversation", requestOptions)
      .then((response) => response.json())
      .then((result) => setSummary(result?.choices?.[0]?.text?.trim()))
      .catch((error) => console.log("error", error))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    dispatch(setPageType(PageType.CallCenter));
    dispatch(setPageTitle("Call Center"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useNavigate();

  const bgStyles: CSSProperties = {
    ...(FSI_DEMOS.includes(currentDemo.guid) && {
      backgroundImage:
        "url(https://dreamdemoassets.blob.core.windows.net/openai/fsi_call_bg.png)",
      backgroundSize: "cover",
    }),
  };

  return (
    <>
      <Popup
        showPopup={visible}
        onClose={() => {
          setVisible(false);
          setIsEnded(false);
          setSummary("");
          navigate(`/${currentDemo.guid}/call-center`);
        }}
        className={styles.popup}
        title="Customer Conversation"
      >
        {isEnded ? (
          <>
            <div className={styles.summaryContainer} style={bgStyles}>
              <div className={styles.avatar}>
                <h2>Agent</h2>
                <img
                  src={`${BlobBaseUrl}${currentDemo?.callCenterAgentImage}`}
                  alt="agent"
                />
              </div>
              <div className={styles.summary}>
                <Button onClick={getSummary} className={styles.summaryBtn}>
                  Summarize Conversation
                </Button>
                {isLoading ? (
                  <div className={styles.loaderContainer}>
                    <Loader themeColor="light" />
                  </div>
                ) : (
                  summary && (
                    <div className={styles.conversationContainer}>
                      <h2
                        style={{
                          textAlign: "center",
                          marginTop: 10,
                          fontSize: "1.5rem",
                        }}
                      >
                        Powered by Azure OpenAI Services
                      </h2>
                      <TextArea
                        value={summary}
                        onChange={(e) => setSummary(e.value)}
                        autoSize={true}
                        className={styles.textArea}
                      />
                    </div>
                  )
                )}
              </div>
              <div className={styles.avatar}>
                <h2>Customer</h2>
                <img
                  src={`${BlobBaseUrl}${currentDemo?.callCenterCustomerImage}`}
                  alt="customer"
                />
              </div>
              <div className={styles.avatar}>
                <h2>Backend</h2>
                <img
                  src={`${BlobBaseUrl}${currentDemo?.callCenterBackendImage}`}
                  alt="customer"
                />
                {summary && (
                  <Button
                    onClick={() => {
                      setVisible(false);
                      setIsEnded(false);
                      setSummary("");
                      navigate(`/${currentDemo.guid}/call-center`);
                    }}
                    className={styles.summaryBtn}
                  >
                    Send to Application
                  </Button>
                )}
              </div>
              <img
                style={{ position: "absolute", bottom: 40 }}
                src={`${BlobBaseUrl}feedback_bar.png`}
                alt="agent"
              />
            </div>
          </>
        ) : (
          <Video
            autoPlay={false}
            src={currentDemo?.callCenterVideoURL}
            pageTitle={"Call Center"}
            pageType={PageType.CallCenterRalph}
            setIsEnded={setIsEnded}
          />
        )}
      </Popup>
    </>
  );
};
